import React, { useState } from 'react';
import { EnhancedDisplayType } from '@components/widgets/dynamic-display/EnhancedDisplay';
import { IDynamicDiplayView } from '@components/widgets/dynamic-display';
import DynamicWidget from '@components/widgets/dynamic-display/DynamicWidget';
import { IAbstractRecord } from '@models';
import EnhancedButton from '@components/button/EnhancedButton';
import CostChargesDetailsDrawer from '@modules/production/policy/page/tabs/amendments-tab/cost-charges-details-drawer/CostChargesDetailsDrawer';
import { IAmendmentGeneralData } from '../line-amendment';

interface IProductionCostChargesWidgetProps {
  data: IAbstractRecord;
  lineExternalCode: string;
  generalData: IAmendmentGeneralData;
  allowEndorsement: boolean;
  onDataRefresh?: () => void;
}

const CancellationCostsAndCharges: React.FC<
  IProductionCostChargesWidgetProps
> = ({
  data,
  lineExternalCode,
  generalData,
  allowEndorsement,
  onDataRefresh,
}) => {
  const [drawerOpen, setDrawerOpen] = useState(false);

  const pricingOptionCurrency =
    data?.amendmentPOVariance_PricingOptionCurrency?.Symbol || '';
  const isOverriden = data?.amendmentPOVariance_isOverriden;

  const editableLineChargesIDs =
    data?.policy_BusinessUserID?.views?.SalesforceManagement_all?.properties?.EditableLineChargesIDs?.map(
      (record: {
        views: {
          PlanConfigManagement_all: { properties: { ExternalCode: string } };
        };
      }) => record?.views?.PlanConfigManagement_all?.properties?.ExternalCode
    );

  const overrideAllowedLines = [
    '4',
    '50',
    '51',
    '43',
    '12',
    '3',
    '33',
    '20',
    '5',
    '9',
    '10',
    '35',
    '13',
    '21',
    '11',
    '15',
    '16',
    '25',
    '27',
    '29',
    '30',
    '31',
    '37',
    '38',
    '39',
    '42',
    '46',
    '2',
    '23',
    '26',
    '8',
  ];

  const dynamicView: IDynamicDiplayView = {
    name: 'CostAndCharges',
    sections: [
      {
        name: 'CostAndCharges',
        title: 'Costs and Charges',
        properties: {
          policyCost: {
            multiline: false,
            value: data?.pricingOption_PolicyCostAmount,
            name: 'policyCost',
            title: 'Policy Cost',
            type: EnhancedDisplayType.RoundedCurrency,
            currencySymbol: pricingOptionCurrency,
          },
          amendmentCost: {
            multiline: false,
            value: data.amendmentPOVariance_AmendmentCost?.toString(),
            name: 'amendmentCost',
            title: 'Amendment Cost',
            type: EnhancedDisplayType.RoundedCurrency,
            currencySymbol: pricingOptionCurrency || '',
            tooltip: isOverriden,
            tooltipContents: <span>Overridden values</span>,
          },
          chargesPercentage: {
            multiline: false,
            value: data.pricingOption_ChargesPercentage?.toString(),
            name: 'chargesPercentage',
            title: 'Charges Percentage',
            type: EnhancedDisplayType.RoundedPercentage,
          },
          amendmentChargesPercentage: {
            multiline: false,
            value:
              data.amendmentPOVariance_AmendmentChargesPercentage?.toString(),
            name: 'amendmentChargesPercentage',
            title: 'Charges Percentage Variance',
            type: EnhancedDisplayType.RoundedPercentage,
            tooltip: isOverriden,
            tooltipContents: <span>Overridden values</span>,
          },
          tpaFees: {
            multiline: false,
            value: data.pricingOption_TPAFeesAmount?.toString(),
            name: 'tpaFees',
            title: 'TPA Fees',
            type: EnhancedDisplayType.RoundedCurrency,
            currencySymbol: pricingOptionCurrency,
            hidden: ['19', '48'].includes(lineExternalCode),
          },
          amendmentTPAFeesAmount: {
            multiline: false,
            value: data.amendmentPOVariance_AmendmentTPAFeesAmount?.toString(),
            name: 'amendmentTPAFeesAmount',
            title: 'TPA Fees Variance',
            type: EnhancedDisplayType.RoundedCurrency,
            currencySymbol: pricingOptionCurrency,
            hidden: ['19', '48'].includes(lineExternalCode),
            tooltip: isOverriden,
            tooltipContents: <span>Overridden values</span>,
          },
        },
      },
    ],
  };

  return (
    <>
      <DynamicWidget
        hasFourFields={true}
        view={dynamicView}
        actions={
          allowEndorsement &&
          overrideAllowedLines.includes(generalData.line.externalCode) && (
            <EnhancedButton
              isPrimary
              disabled={
                ['COMPLETED', 'CANCELLED'].includes(
                  generalData.amendment.status
                ) ||
                !editableLineChargesIDs.includes(generalData.line.externalCode)
              }
              onClick={() => setDrawerOpen(true)}
            >
              Override
            </EnhancedButton>
          )
        }
      />

      {drawerOpen && (
        <CostChargesDetailsDrawer
          open={drawerOpen}
          onClose={() => setDrawerOpen(false)}
          data={data}
          lineExternalCode={lineExternalCode}
          onSuccess={() => {
            onDataRefresh();
          }}
        />
      )}
    </>
  );
};

export default CancellationCostsAndCharges;
